import type { Content } from 'mdast';

/**
 * AI-2027 magazine handling of expandable `:::details` boxes: instead of an
 * inline collapsible box, each one becomes a cross-reference in the flow
 * ("➤ See Appendix C — <title> for more detail.") and its body is collected
 * into an Appendices section at the back of the document.
 *
 * We do this by rewriting the parsed markdown AST before it reaches the
 * renderer: every `details` node is replaced by a raw-`html` cross-reference
 * node (the renderer's `html` case renders it verbatim), and the body is
 * pushed onto an ordered appendix list keyed by a letter (A, B, C…).
 */

export interface Appendix {
  letter: string;
  id: string;
  title: string;
  children: Content[];
}

// A, B, …, Z, AA, AB, … (matches Typst's `counter.display("A")`).
function letterFor(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isDetails(node: Content): boolean {
  return (node as { type?: string }).type === 'details';
}

function isDirective(node: Content, name: string): boolean {
  const n = node as { type?: string; name?: string };
  return n.type === 'containerDirective' && n.name === name;
}

function getText(node: unknown): string {
  const n = node as { value?: string; children?: unknown[] };
  if (typeof n?.value === 'string') return n.value;
  if (Array.isArray(n?.children)) return n.children.map(getText).join('');
  return '';
}

/**
 * AI-2027-style details have a short summary (title) + a real body that becomes
 * the appendix. ai-2030 also uses `:::details` with only a summary paragraph
 * (no body) as inline caption/aside boxes — those should stay in place, not
 * become appendices. Only extract details that actually have body content.
 */
function hasBody(node: Content): boolean {
  const d = node as unknown as { children?: Content[] };
  return Array.isArray(d.children) && d.children.length > 0;
}

/**
 * Walk the tree in document order, replacing details nodes with cross-refs and
 * accumulating appendices. Nested details (a details inside a details body) are
 * flattened in encounter order, just like the linear appendix list in AI-2027.
 */
export function extractDetailsToAppendices(nodes: Content[]): {
  nodes: Content[];
  appendices: Appendix[];
} {
  const appendices: Appendix[] = [];
  // Increments at encounter time (before recursing into the body) so a parent
  // claims its letter and slot before any nested details inside it.
  let count = 0;

  const crossRef = (appendix: Appendix): Content =>
    ({
      type: 'html',
      value:
        `<div class="appendix-ref">➤ See ` +
        `<a href="#${appendix.id}" class="appendix-ref-title">` +
        `Appendix ${appendix.letter} — ${escapeHtml(appendix.title)}</a>` +
        ` for more detail.</div>`,
    }) as unknown as Content;

  // A `:::branchpoint` is an interactive "alternate timeline" box whose body is
  // hidden behind a dead "Read this timeline" control in print. Replace it with
  // a labeled aside that cross-references the appendix holding the full branch.
  const branchAside = (appendix: Appendix): Content =>
    ({
      type: 'html',
      value:
        `<div class="branch-aside">` +
        `<div class="branch-aside-label">Alternate Timeline</div>` +
        `<a href="#${appendix.id}" class="branch-aside-title">${escapeHtml(appendix.title)}</a>` +
        `<div class="branch-aside-ref">➤ See Appendix ${appendix.letter} for this branch.</div>` +
        `</div>`,
    }) as unknown as Content;

  const reserve = (title: string): Appendix => {
    const letter = letterFor(count++);
    const appendix: Appendix = {
      letter,
      id: `appendix-${letter.toLowerCase()}`,
      title: title.trim() || 'Appendix',
      children: [],
    };
    appendices.push(appendix);
    return appendix;
  };

  const walk = (list: Content[]): Content[] =>
    list.map((node) => {
      if (isDirective(node, 'branchpoint')) {
        const children = (node as { children?: Content[] }).children ?? [];
        const summary = children.find((c) => isDirective(c, 'branchSummary'));
        const summaryKids =
          (summary as { children?: Content[] } | undefined)?.children ?? [];
        const headingIdx = summaryKids.findIndex(
          (c) => (c as { type?: string }).type === 'heading',
        );
        const title =
          headingIdx >= 0 ? getText(summaryKids[headingIdx]) : 'Alternate Timeline';
        const premise = headingIdx >= 0 ? summaryKids.slice(headingIdx + 1) : summaryKids;
        const body = children.filter((c) => c !== summary);
        const appendix = reserve(title);
        // The appendix is self-contained: the branch premise, then its body.
        appendix.children = walk([...premise, ...body]);
        return branchAside(appendix);
      }
      // A `:::policyList` (the Incremental AI Policy Wishlist) is a full
      // policy section that would interrupt the scenario flow — pull it out
      // as a lettered appendix like any details box. The title comes from
      // the block's own H1, which is dropped (the appendix header carries it).
      if (isDirective(node, 'policyList')) {
        const children = (node as { children?: Content[] }).children ?? [];
        const h1Index = children.findIndex((c) => {
          const h = c as { type?: string; depth?: number };
          return h.type === 'heading' && h.depth === 1;
        });
        const title =
          h1Index >= 0 ? getText(children[h1Index]) : 'Policy Wishlist';
        const appendix = reserve(title);
        appendix.children = walk(children.filter((_, i) => i !== h1Index));
        return crossRef(appendix);
      }
      if (isDetails(node) && hasBody(node)) {
        const d = node as unknown as {
          summary?: string;
          summaryLines?: string[];
          children: Content[];
        };
        const title =
          (d.summaryLines && d.summaryLines.join(' ')) || d.summary || 'Details';
        // Reserve the slot in letter order, THEN recurse so any details nested
        // inside this body are pulled out and land after this entry.
        const appendix = reserve(title);
        appendix.children = walk(d.children);
        return crossRef(appendix);
      }
      const parent = node as unknown as { children?: Content[] };
      if (Array.isArray(parent.children)) {
        return { ...node, children: walk(parent.children) } as Content;
      }
      return node;
    });

  const transformed = walk(nodes);
  return { nodes: transformed, appendices };
}
